import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";

const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="15" height="8" viewBox="0 0 23 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path stroke="currentColor" d="M8.73823 9.70349C9.0194 9.95308 9.33455 10 9.50032 10C9.68532 10 9.97862 9.9545 10.2614 9.70349L18.7262 1.54155C19.1137 1.16804 19.0866 0.58669 18.666 0.242975C18.245 -0.100919 17.5903 -0.0759709 17.2033 0.296641L9.5 7.72292L1.79695 0.296641C1.40913 -0.0770478 0.754206 -0.10074 0.334037 0.242975C-0.0865365 0.58669 -0.113631 1.16804 0.27358 1.54155L8.73823 9.70349Z" />
  </svg>
);

const getSubmenuIcon = (label) => {
  const cleanLabel = (label || "").toLowerCase();
  
  if (cleanLabel.includes("quant")) {
    // Bar Chart Icon
    return (
      <svg className="submenu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"></line>
        <line x1="12" y1="20" x2="12" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="14"></line>
      </svg>
    );
  }
  
  if (cleanLabel.includes("qual")) {
    // Message/Chat Icon
    return (
      <svg className="submenu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
    );
  }
  
  // Default bullet circle icon
  return (
    <svg className="submenu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
};

export const SubMenuGroup = ({
  data,
  isOpen,
  setIsOpen,
  onSidebarToggle,
  hasPermission,
  activeMenuRef,
  customSubItems,
}) => {
  const currentPath = window.location.pathname;
  const isParentDisabled = !!data?.isDisabled;
  const storageKey = `submenu_open_${data.caption.toLowerCase()}`;

  // Resolve sub-items list
  const dynamicSubItems = data.sub_menu || [];
  const itemsToRender = customSubItems || dynamicSubItems;

  // Active state validation
  const hasActiveChild = itemsToRender.some((sub) => {
    const path = sub.path || `/${sub.url}`;
    return currentPath === path || currentPath.startsWith(path + "/");
  });

  const [isExpanded, setIsExpanded] = useState(() => {
    const storedState = localStorage.getItem(storageKey);
    return storedState ? JSON.parse(storedState) : hasActiveChild;
  });

  useEffect(() => {
    if (hasActiveChild) {
      setIsExpanded(true);
      localStorage.setItem(storageKey, JSON.stringify(true));
    }
  }, [currentPath, hasActiveChild, storageKey]);

  const handleToggle = () => {
    if (isParentDisabled) return;
    if (!isOpen) {
      setIsOpen(true);
      if (onSidebarToggle) onSidebarToggle(true);
      setIsExpanded(true);
      localStorage.setItem(storageKey, JSON.stringify(true));
    } else {
      const nextState = !isExpanded;
      setIsExpanded(nextState);
      localStorage.setItem(storageKey, JSON.stringify(nextState));
    }
  };

  // Filter items using permission rules if cap/code are specified (used by legacy hardcoded modules)
  const filteredSubItems = itemsToRender.filter((sub) => {
    if (sub.cap && sub.code) {
      return hasPermission(sub.cap, sub.code);
    }
    return true;
  });

  // If no child items are authorized, do not show the parent menu
  if (filteredSubItems.length === 0) {
    return null;
  }

  return (
    <li ref={hasActiveChild ? activeMenuRef : null}>
      <div
        onClick={handleToggle}
        className="audit-module list-group-item-action list-group-item-light nop"
        style={{
          display: "flex",
          alignItems: "center",
          borderRadius: "5px",
          transition: "background-color 0.3s ease",
          cursor: isParentDisabled ? "not-allowed" : "pointer",
        }}
      >
        <span className="sidebar-icon-wrap">
          <img className="icon-inactive" src={`/sidebar-icons/${data?.inactiveIcon}`} width={32} height={32} alt="" />
          <img className="icon-active" src={`/sidebar-icons/${data?.activeIcon}`} width={32} height={32} alt="" />
        </span>
        <span className="home_boom" style={isParentDisabled ? { color: "#999" } : {}}>{data?.caption}</span>
        {isParentDisabled ? (
          isOpen && <span style={{ marginLeft: "auto", color: "#bbb", display: "flex", alignItems: "center" }}><LockIcon /></span>
        ) : (
          <div
            style={{
              marginLeft: "auto",
              color: "#3F88A5",
              transform: isExpanded ? "rotate(0deg)" : "rotate(90deg)",
              transition: "transform 0.3s ease",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ArrowIcon />
          </div>
        )}
      </div>
      {(isParentDisabled || isExpanded) && (
        <ul className="submenu" style={{ marginTop: "6px" }}>
          {filteredSubItems.map((sub, idx) => {
            const subDisabled = !!sub.isDisabled;
            const path = sub.path || `/${sub.url}`;
            const subIcon = sub.icon || getSubmenuIcon(sub.label || sub.caption);

            return (
              <li key={idx} className="submenu-item">
                {subDisabled ? (
                  <div
                    className="list-group-item list-group-item-action list-group-item-light nop"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 12px",
                      cursor: "not-allowed",
                    }}
                  >
                    {subIcon}
                    <span style={{ flex: 1, color: "#999" }}>{sub.label || sub.caption}</span>
                    <span style={{ color: "#bbb", display: "flex", alignItems: "center" }}><LockIcon /></span>
                  </div>
                ) : (
                  <NavLink to={{ pathname: path, state: {} }} className="list-group-item list-group-item-action list-group-item-light nop">
                    {subIcon}<span>{sub.label || sub.caption}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};
